import { formatISO, isValid, parseISO } from 'date-fns';

export const nameof = <T>(name: keyof T) => name;

export const getEmailSuffixIndex: (
  email: string,
  suffixes: string[]
) => number = (email: string, suffixes: string[]) => {
  // Split the email by '@'
  const emailParts = email.split('@');
  // Get the email suffix
  const emailSuffix = emailParts[emailParts.length - 1];
  // Search for the email suffix in the suffixes array
  return suffixes.indexOf(emailSuffix);
};

export const getEmailprefix: (email: string) => string = (email: string) => {
  // Split the email by '@'
  const emailParts = email.split('@');
  return emailParts[0];
};

export const getCurrencySymbol = (isoCode: string, language: string) => {
  if (!isoCode || isoCode.length !== 3 || !language) return null;
  try {
    return (0)
      .toLocaleString(language, {
        style: 'currency',
        currency: isoCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim();
  } catch (error) {
    console.error(error);
    return '€';
  }
};

/**
 * Checks whether the data is older than the specified time and needs to be refreshed.
 * @param lastFetch ISO timestamp of the last fetch.
 * @param cacheMinutes Cache duration in minutes (default: 5 minutes).
 * @returns true if a refresh is required.
 */
export const shouldRefreshCache = (
  lastFetch?: string,
  cacheMinutes: number = 5
): boolean => {
  if (!lastFetch) return true;

  const now = new Date();
  const lastFetchDate = new Date(lastFetch);
  const diffMinutes = (now.getTime() - lastFetchDate.getTime()) / (1000 * 60);

  return diffMinutes >= cacheMinutes;
};

export const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return date.toLocaleDateString(navigator.language ?? 'de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatDateTime = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '';
  return date.toLocaleString(navigator.language ?? 'de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Converts a date value to an ISO string in the format "yyyyMMdd'T'HH:mm:ss".
 * Handles Date objects, ISO strings, and timestamps. Returns current date/time if invalid.
 * @param date Date object, ISO date string, or timestamp
 * @param options Optional settings:
 *   - keepOffset: If true, keeps the original timezone offset (default: true)
 *   - slice: If true, slices milliseconds and timezone info from the ISO string (default: true)
 * @returns ISO formatted date string or current date/time if invalid
 *
 * @example
 * toISOString(new Date()) // "2024-06-17T14:30:00"
 * toISOString("2024-06-17T14:30:00Z") // "2024-06-17T14:30:00"
 * toISOString(1710000000000) // "2024-06-17T14:30:00"
 * toISOString("invalid date") // current date/time in ISO format
 */
export const toISOString = (
  date: string | number | Date,
  options?: {
    keepOffset?: boolean;
    slice?: boolean;
  }
): string => {
  const { keepOffset = true, slice = true } = options || {};

  const convertValidDateToString = (date: Date): string => {
    let isoString = date.toISOString();
    try {
      if (keepOffset) {
        isoString = formatISO(date);
      }
      if (slice) {
        isoString = isoString.slice(0, 19);
      }
      return isoString;
    } catch {
      return isoString;
    }
  };

  try {
    const parsedDate =
      typeof date === 'string'
        ? parseISO(date)
        : date instanceof Date
          ? date
          : new Date(date);

    if (!isValid(parsedDate)) {
      return convertValidDateToString(new Date());
    }

    return convertValidDateToString(parsedDate);
  } catch {
    return convertValidDateToString(new Date());
  }
};
